import React, { memo, useCallback } from 'react';
import {
  Button,
  InputAdornment,
  InputBase,
  MenuItem,
  Select
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import { useDebouncedCallback } from 'use-debounce/lib';
import { useMessage, useMessages } from 'modules/intl';
import { useCollectorSelectionContext } from './contexts/CollectorSelectionContextProvide';

const useStyles = makeStyles((theme) => ({
  input: {
    backgroundColor: theme.palette.grey[300],
    width: '80%',
    borderRadius: theme.spacing(0.5),
    '& .MuiInputBase-input::placeholder': {
      opacity: 0.9,
      color: '#424242'
    },
    fontWeight: 600,
    color: '#424242',
    fontSize: theme.typography.body2.fontSize
  },
  iconSearch: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(-1)
  },
  topContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    height: theme.spacing(4.5),
    margin: theme.spacing(2),
    marginLeft: theme.spacing(2.8)
  },
  select: {
    backgroundColor: theme.palette.common.white,
    width: '17%'
  },
  menuItem: {
    '&.Mui-selected': {
      backgroundColor: theme.palette.grey[200]!,
      '&:hover': {
        backgroundColor: theme.palette.grey[100]!
      }
    }
  },
  clearIcon: {
    color: theme.palette.grey[600]
  }
}));

type CollectorHeaderProps = {
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
};

const selectMessages = [
  'collectors.all',
  'collectors.installed',
  'collectors.notInstalled'
];

export const CollectorHeader: React.FC<CollectorHeaderProps> = memo(
  ({ setSearchValue }: CollectorHeaderProps) => {
    const classes = useStyles();
    const selectValues = useMessages(selectMessages);
    const {
      collectorsSelectionStatus,
      setCollectorsSelectionStatus
    } = useCollectorSelectionContext();

    const onSelectChange = useCallback(
      (event) => {
        setCollectorsSelectionStatus(event.target.value);
      },
      [setCollectorsSelectionStatus]
    );
    const searchText = useMessage('collectors.search');

    const [search, setSearch] = React.useState('');

    const onSearch = useDebouncedCallback((input) => {
      setSearchValue(input);
    }, 1000);

    const handleSearch = useCallback(
      (event) => {
        setSearch(event.target.value || '');
        onSearch(event.target.value || '');
      },
      [onSearch]
    );

    const handleClearSearch = useCallback(
      (event) => {
        onSearch(event.target.value || '');
        setSearch(event.target.value || '');
      },
      [onSearch]
    );

    return (
      <div className={classes.topContainer} data-testid="collectorAllPage">
        <InputBase
          className={classes.input}
          placeholder={searchText}
          inputProps={{
            'aria-label': searchText,
            'data-testid': 'search-collector'
          }}
          value={search}
          startAdornment={
            <InputAdornment position="start">
              <SearchSharpIcon className={classes.iconSearch} />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              {search.length > 0 && (
                <Button onClick={handleClearSearch} data-testid="remove-search">
                  <ClearSharpIcon className={classes.clearIcon} />
                </Button>
              )}
            </InputAdornment>
          }
          fullWidth
          data-testid="collectorSearch"
          onChange={handleSearch}
        />
        <Select
          id="collector-select"
          value={collectorsSelectionStatus}
          className={classes.select}
          onChange={onSelectChange}
          data-testid="collectorSelect"
        >
          {Object.keys(selectValues).map((i) => {
            return (
              <MenuItem
                value={i}
                key={i}
                className={classes.menuItem}
                data-testid={`collectorSelectOption-${i}`}
              >
                {selectValues[i]}
              </MenuItem>
            );
          })}
        </Select>
      </div>
    );
  }
);
CollectorHeader.displayName = 'CollectorHeader';
